import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { authClient } from "../lib/auth";
import { useOrgAccess, useOrgCan } from "../lib/access";
import { track } from "../lib/track";
import { Button, Card, PageHeader, notifyError } from "../components/ui";
import { usePageTitle } from "../lib/page-title";
import { parseDomains } from "../lib/onboarding";
import { AppFields, useCreateApp, type AppDraft } from "./onboarding";

/** Another app in a workspace that already has one. The first app is added during set-up (/onboarding/app). */
export function NewApp() {
  usePageTitle("Add an app");
  const active = authClient.useActiveOrganization();
  const session = authClient.useSession();
  const navigate = useNavigate();
  const orgId = active.data?.id;
  const workspace = active.data?.name ?? "";
  const access = useOrgAccess(orgId);
  const canAdd = useOrgCan(orgId)("apps.create");
  const create = useCreateApp(orgId);
  const [draft, setDraft] = useState<AppDraft>({ platform: "web", name: "", domains: "" });
  const [nameError, setNameError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      setNameError("Enter a name for the app.");
      return;
    }
    try {
      const app = await create.mutateAsync({
        name: draft.name.trim(),
        platform: draft.platform,
        domains: parseDomains(draft.domains),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      track("app_created", { platform: draft.platform, domains: app.domains.length });
      navigate(`/onboarding/install/${app.id}`);
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="max-w-xl">
      <PageHeader title="Add an app" subtitle={`In ${workspace || "your workspace"}. You'll get the code to install next.`} />
      {access.data && !canAdd ? (
        <Card className="p-5">
          <p className="text-sm text-ink-2">You can't add apps in this workspace. Ask a workspace admin to add one, or for permission to add apps.</p>
        </Card>
      ) : (
        <Card className="p-5">
          <form onSubmit={submit} noValidate className="space-y-5">
            <AppFields
              workspace={workspace}
              email={session.data?.user.email}
              value={draft}
              onChange={(v) => {
                setDraft(v);
                if (v.name.trim()) setNameError(null);
              }}
              nameError={nameError}
            />
            <Button variant="primary" pending={create.isPending} pendingLabel="Adding…" disabled={!orgId}>
              Add app
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
